import React, { useMemo, useState } from 'react';
import { Box, Container, Header, Spinner, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-core';
import type Highcharts from 'highcharts';
import { HighchartsChart } from '../charts/HighchartsChart';
import { CONNECTIONS_THEMES, THEME_DISPLAY_NAMES, Theme } from '../types';
import { useConnectionsTrends } from '../useConnectionsData';
import { CONNECTIONS_COLORS } from '../connectionsColors';
import { formatMonthAbbrev } from 'src/utilities/DateTimeUtilities';

interface MultiThemeTrendProps {
  leaderLogin: string;
  scope: string;
  summaryType: string;
  infoLink?: React.ReactNode;
  sectionContext?: string;
}

// Month labels reuse the shared DateTimeUtilities helper (year is arbitrary; only the MMM abbrev is used).
const MONTH_LABELS = Array.from({ length: 12 }, (_, i) => formatMonthAbbrev(2000, i));

// Previous year = muted/dashed, current year = accent/solid.
const PREV_YEAR_COLOR = CONNECTIONS_COLORS.MUTED_HIDDEN;
const CURR_YEAR_COLOR = CONNECTIONS_COLORS.SERIES_ACCENT;

const THEME_SELECT_OPTIONS: SelectProps.Option[] = CONNECTIONS_THEMES.map((t) => ({
  label: THEME_DISPLAY_NAMES[t],
  value: t
}));

/**
 * Year-over-year theme trend for the selected leader.
 *
 * X axis = calendar months (Jan-Dec), Y axis = % positive (adjusted PRR).
 * One theme at a time (theme selector); two lines - previous year vs current year -
 * so seasonality and YoY movement read at a glance. Uses T3M points (org-wide coverage),
 * bucketed by the point's start month/year.
 *
 * All four themes are fetched up front so switching the selector is instant (no refetch).
 * Dark mode handled automatically by the HighchartsChart wrapper.
 */
export const MultiThemeTrend: React.FC<MultiThemeTrendProps> = ({ leaderLogin, scope, summaryType, infoLink, sectionContext }) => {
  const careerGrowth = useConnectionsTrends(leaderLogin || null, 'CAREER_GROWTH', scope, summaryType);
  const culture = useConnectionsTrends(leaderLogin || null, 'CULTURE', scope, summaryType);
  const inclusion = useConnectionsTrends(leaderLogin || null, 'INCLUSION', scope, summaryType);
  const jobSat = useConnectionsTrends(leaderLogin || null, 'JOB_SATISFACTION', scope, summaryType);

  const [selectedTheme, setSelectedTheme] = useState<Theme>(CONNECTIONS_THEMES[0]);

  const themeStates = useMemo(
    () => ({
      CAREER_GROWTH: careerGrowth,
      CULTURE: culture,
      INCLUSION: inclusion,
      JOB_SATISFACTION: jobSat
    }),
    [careerGrowth, culture, inclusion, jobSat]
  );

  const isLoading = Object.values(themeStates).some((s) => s.loading);
  const error = Object.values(themeStates).find((s) => s.error)?.error;

  // YoY series for the SELECTED theme: two lines (prev year, current year) x 12 calendar months.
  const { series, currentYear } = useMemo(() => {
    const pts = (themeStates[selectedTheme].data || []).filter((d) => d.metricPeriod === 'MONTHLY_TRAILING_THREE_MONTHS' && d.metricValue != null);
    if (!pts.length) return { series: [] as Highcharts.SeriesOptionsType[], currentYear: null as number | null };

    const years = [...new Set(pts.map((p) => new Date(p.startDate).getUTCFullYear()))].sort((a, b) => a - b);
    const curr = years[years.length - 1];
    const prev = curr - 1;

    const buildYear = (yr: number): (number | null)[] => {
      const arr: (number | null)[] = Array(12).fill(null);
      pts.forEach((p) => {
        const d = new Date(p.startDate);
        if (d.getUTCFullYear() === yr) arr[d.getUTCMonth()] = Math.round((p.metricValue as number) * 1000) / 10;
      });
      return arr;
    };

    const out: Highcharts.SeriesOptionsType[] = [];
    const prevArr = buildYear(prev);
    if (prevArr.some((v) => v != null)) {
      out.push({
        type: 'spline',
        name: String(prev),
        color: PREV_YEAR_COLOR,
        dashStyle: 'ShortDash',
        data: prevArr,
        connectNulls: true,
        marker: { enabled: true, radius: 3, symbol: 'circle' },
        lineWidth: 2
      } as Highcharts.SeriesSplineOptions);
    }
    out.push({
      type: 'spline',
      name: String(curr),
      color: CURR_YEAR_COLOR,
      data: buildYear(curr),
      connectNulls: true,
      marker: { enabled: true, radius: 4, symbol: 'circle' },
      lineWidth: 2.5
    } as Highcharts.SeriesSplineOptions);

    return { series: out, currentYear: curr };
  }, [themeStates, selectedTheme]);

  const options = useMemo<Highcharts.Options>(
    () => ({
      chart: { type: 'spline' },
      xAxis: {
        categories: MONTH_LABELS,
        title: { text: undefined },
        labels: { style: { fontSize: '11px' } }
      },
      yAxis: {
        min: 0,
        max: 100,
        labels: { format: '{value}%' },
        title: { text: '% positive (adjusted PRR)' }
      },
      tooltip: {
        shared: true,
        valueSuffix: '%',
        valueDecimals: 1,
        headerFormat: '<span style="font-size:11px">{point.key}</span><br/>'
      },
      plotOptions: {
        spline: { states: { hover: { lineWidth: 3.5 } } }
      },
      legend: { align: 'center', verticalAlign: 'bottom' },
      series
    }),
    [series]
  );

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={<span style={{ fontSize: '11px', color: CONNECTIONS_COLORS.SECTION_CONTEXT }}>{sectionContext}</span>}
          description="Year-over-year by calendar month; pick a theme to compare this year vs last (trailing 3-month windows)"
          info={infoLink}
        >
          📈 Theme Trend Over Time
        </Header>
      }
    >
      <SpaceBetween size="m">
        <div style={{ maxWidth: 260 }}>
          <Select
            selectedOption={THEME_SELECT_OPTIONS.find((o) => o.value === selectedTheme) || null}
            onChange={({ detail }) => setSelectedTheme(detail.selectedOption.value as Theme)}
            options={THEME_SELECT_OPTIONS}
            ariaLabel="Select theme"
          />
        </div>

        {isLoading && (
          <Box textAlign="center" padding="xl">
            <Spinner size="big" /> Loading trends...
          </Box>
        )}
        {error && !isLoading && (
          <Box color="text-status-error" padding="m">
            Failed to load trends: {error.message}
          </Box>
        )}
        {!isLoading && !error && series.length === 0 && (
          <Box color="text-body-secondary" padding="m">
            No trend data available for {THEME_DISPLAY_NAMES[selectedTheme]} for this leader.
          </Box>
        )}
        {!isLoading && !error && series.length === 1 && currentYear != null && (
          <Box color="text-body-secondary" fontSize="body-s" padding={{ left: 's' }}>
            Only {currentYear} data is available for this leader - no prior-year line to compare yet.
          </Box>
        )}
        {!isLoading && !error && series.length > 0 && <HighchartsChart options={options} height={360} />}
      </SpaceBetween>
    </Container>
  );
};
